import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api, artifactFileUrl } from '@web/api/client'
import type { SessionArtifact, SessionArtifactFile } from '@web/api/types'
import { uploadSessionFile } from '@web/api/uploads'
import { Icon } from '@web/components/ui/icons'
import { useDialogFocus } from '@web/lib/useDialogFocus'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import '@web/components/session-artifacts.css'

type ArtifactSelection = {
  sessionId: string
  artifactId?: string
  fileId?: string
}

/** Open the host's authenticated artifact browser from any tool presentation. */
export function openSessionArtifact(selection: ArtifactSelection) {
  window.dispatchEvent(
    new CustomEvent('bridge:session-artifact', { detail: selection }),
  )
}

export function SessionArtifacts({ sessionId }: { sessionId: string }) {
  const [selection, setSelection] = useState<ArtifactSelection | null>(null)
  useEffect(() => {
    const inspect = (event: Event) => {
      const next = (event as CustomEvent<ArtifactSelection>).detail
      if (next?.sessionId === sessionId) setSelection(next)
    }
    window.addEventListener('bridge:session-artifact', inspect)
    return () => window.removeEventListener('bridge:session-artifact', inspect)
  }, [sessionId])
  return (
    <>
      <button
        type="button"
        className="btn btn-sm session-artifacts-button"
        onClick={() => setSelection({ sessionId })}
      >
        <Icon name="fileText" size={14} />
        Files
      </button>
      {selection && (
        <ArtifactBrowser
          key={`${selection.sessionId}:${selection.artifactId ?? ''}`}
          selection={selection}
          onClose={() => setSelection(null)}
        />
      )}
    </>
  )
}

function ArtifactBrowser({
  selection,
  onClose,
}: {
  selection: ArtifactSelection
  onClose: () => void
}) {
  const { snapshot } = useBridge()
  const dialog = useRef<HTMLDialogElement>(null)
  const heading = useId()
  const active = useRef(true)
  const loadVersion = useRef(0)
  const [kind, setKind] = useState<'artifacts' | 'uploads'>('artifacts')
  const [uploading, setUploading] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState('')
  const [artifacts, setArtifacts] = useState<SessionArtifact[]>([])
  const [artifactId, setArtifactId] = useState(selection.artifactId ?? '')
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  useDialogFocus(dialog, onClose)
  const load = useCallback(async () => {
    const version = ++loadVersion.current
    setBusy(true)
    setError('')
    try {
      const items =
        kind === 'uploads'
          ? (await api.sessionUploads(selection.sessionId)).uploads
          : (await api.sessionArtifacts(selection.sessionId)).artifacts
      if (active.current && version === loadVersion.current) setArtifacts(items)
    } catch (failure) {
      if (active.current && version === loadVersion.current)
        setError((failure as Error).message)
    } finally {
      if (active.current && version === loadVersion.current) setBusy(false)
    }
  }, [selection.sessionId, kind])
  useEffect(() => {
    active.current = true
    const element = dialog.current
    element?.showModal()
    return () => {
      active.current = false
      element?.close()
    }
  }, [])
  useEffect(() => {
    void load()
    if (!snapshot.acp.running) return
    const timer = window.setInterval(() => void load(), 5000)
    return () => window.clearInterval(timer)
  }, [load, snapshot.acp.running])
  const uploadFiles = async (files: File[]) => {
    setUploading(true)
    setUploadError('')
    const failed: File[] = []
    for (const file of files) {
      try {
        const { upload } = await uploadSessionFile(selection.sessionId, file)
        if (active.current) setArtifactId(upload.id)
      } catch (failure) {
        failed.push(file)
        if (active.current) setUploadError((failure as Error).message)
      }
    }
    if (active.current) {
      setPendingFiles(failed)
      setUploading(false)
      await load()
    }
  }
  const artifact = artifacts.find((item) => item.id === artifactId)
  return createPortal(
    <dialog
      ref={dialog}
      className="artifact-browser"
      aria-labelledby={heading}
      aria-modal="true"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <header className="artifact-browser-heading">
        <div>
          <p className="label">Saved in this session</p>
          <h2 id={heading}>Session files</h2>
        </div>
        <button
          type="button"
          className="btn btn-icon"
          aria-label="Close files"
          onClick={onClose}
        >
          <Icon name="close" size={18} />
        </button>
      </header>
      <div
        className="artifact-file-toolbar artifact-categories"
        aria-label="File categories"
      >
        {(['artifacts', 'uploads'] as const).map((category) => (
          <button
            key={category}
            type="button"
            className="btn btn-sm"
            aria-pressed={kind === category}
            disabled={uploading}
            onClick={() => {
              ++loadVersion.current
              setArtifacts([])
              setArtifactId('')
              setKind(category)
            }}
          >
            {category === 'artifacts' ? 'Generated files' : 'Uploads'}
          </button>
        ))}
      </div>
      <div className="artifact-browser-body">
        <aside className="artifact-library" aria-label="Session file list">
          <div className="artifact-library-heading">
            <span>
              {artifacts.length}{' '}
              {kind === 'uploads'
                ? artifacts.length === 1
                  ? 'upload'
                  : 'uploads'
                : artifacts.length === 1
                  ? 'file'
                  : 'files'}
            </span>
            <button
              type="button"
              className="btn btn-icon"
              aria-label="Refresh files"
              disabled={busy}
              onClick={() => void load()}
            >
              <Icon name="refresh" size={13} className={busy ? 'spin' : ''} />
            </button>
          </div>
          {kind === 'uploads' && (
            <div className="artifact-library-heading">
              <button
                type="button"
                className="btn btn-sm"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                {uploading ? 'Uploading…' : 'Upload files'}
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                aria-label="Upload session files"
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])]
                  event.target.value = ''
                  if (files.length) void uploadFiles(files)
                }}
              />
            </div>
          )}
          {uploadError && (
            <div className="artifact-error" role="alert">
              {uploadError}
              {pendingFiles.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={uploading}
                  onClick={() => void uploadFiles(pendingFiles)}
                >
                  Retry uploads
                </button>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="artifact-error">
              {error}
            </p>
          )}
          {busy && !artifacts.length && (
            <p role="status" className="artifact-empty">
              Loading files…
            </p>
          )}
          {!busy && !error && !artifacts.length && (
            <p className="artifact-empty">
              {kind === 'uploads'
                ? 'Upload files up to 4 MB each. Files you attach to messages are also saved here.'
                : 'Reports and files created during your session will appear here.'}
            </p>
          )}
          {artifacts.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`artifact-library-item${item.id === artifactId ? ' selected' : ''}`}
              aria-pressed={item.id === artifactId}
              onClick={() => setArtifactId(item.id)}
            >
              <Icon name="fileText" size={16} />
              <span>
                <strong>{item.title}</strong>
                <small>
                  {item.files.length}{' '}
                  {item.files.length === 1 ? 'file' : 'files'}
                </small>
              </span>
            </button>
          ))}
        </aside>
        <section
          className="artifact-preview-panel"
          aria-label="Artifact preview"
        >
          {artifact ? (
            <ArtifactPreview
              key={artifact.id}
              artifact={artifact}
              initialFileId={selection.fileId}
              kind={kind}
            />
          ) : (
            <div className="artifact-preview-empty">
              <Icon name="fileText" size={30} />
              <h3>
                {artifactId && !busy ? 'Artifact unavailable' : 'Session files'}
              </h3>
              <p>
                {artifactId && !busy
                  ? 'Refresh the list to check for newly saved files.'
                  : 'Choose a file to preview or download a copy.'}
              </p>
            </div>
          )}
        </section>
      </div>
    </dialog>,
    document.body,
  )
}

/** Reusable file selector and restricted preview for both demo and production tools. */
export function ArtifactPreview({
  artifact,
  initialFileId,
  kind = 'artifacts',
}: {
  artifact: SessionArtifact
  initialFileId?: string
  kind?: 'artifacts' | 'uploads'
}) {
  const [fileId, setFileId] = useState(
    initialFileId ?? artifact.files[0]?.id ?? '',
  )
  const file =
    artifact.files.find((item) => item.id === fileId) ?? artifact.files[0]
  return (
    <div className="artifact-preview">
      <div className="artifact-preview-heading">
        <div>
          <h3>{artifact.title}</h3>
          {artifact.description && <p>{artifact.description}</p>}
        </div>
      </div>
      {file && (
        <>
          <div className="artifact-file-toolbar">
            <label>
              <span className="sr-only">Artifact file</span>
              <select
                aria-label="Artifact file"
                value={file.id}
                onChange={(event) => setFileId(event.target.value)}
              >
                {artifact.files.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.path}
                  </option>
                ))}
              </select>
            </label>
            <span>
              {file.size_bytes < 1024
                ? `${file.size_bytes} B`
                : `${Math.ceil(file.size_bytes / 1024)} KB`}
            </span>
            <a
              className="btn btn-sm"
              href={artifactFileUrl(
                artifact.session_id,
                artifact.id,
                file.id,
                true,
                kind,
              )}
              download
            >
              <Icon name="arrowDown" size={13} />
              Download
            </a>
          </div>
          <ArtifactFilePreview
            key={file.id}
            artifact={artifact}
            file={file}
            kind={kind}
          />
        </>
      )}
    </div>
  )
}

function ArtifactFilePreview({
  artifact,
  file,
  kind,
}: {
  artifact: SessionArtifact
  file: SessionArtifactFile
  kind: 'artifacts' | 'uploads'
}) {
  const url = artifactFileUrl(
    artifact.session_id,
    artifact.id,
    file.id,
    false,
    kind,
  )
  const mime = file.mime_type.split(';')[0]?.trim().toLowerCase() ?? ''
  const textPreview =
    (mime.startsWith('text/') && mime !== 'text/html') ||
    ['application/json', 'application/xml'].includes(mime)
  const framePreview = [
    'text/html',
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
  ].includes(mime)
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    if (!textPreview) return
    const controller = new AbortController()
    void fetch(url, { credentials: 'include', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('This file could not be loaded.')
        const content = await response.text()
        if (!controller.signal.aborted) setText(content)
      })
      .catch((failure: Error) => {
        if (!controller.signal.aborted) setError(failure.message)
      })
    return () => controller.abort()
  }, [url, textPreview])
  if (error)
    return (
      <p role="alert" className="artifact-error">
        {error}
      </p>
    )
  if (textPreview)
    return text === null ? (
      <p role="status" className="artifact-empty">
        Loading preview…
      </p>
    ) : (
      <pre className="artifact-text-preview">{text}</pre>
    )
  if (framePreview)
    return (
      <iframe
        className="artifact-file-frame"
        title={`Preview of ${file.path}`}
        src={url}
        sandbox=""
        referrerPolicy="no-referrer"
      />
    )
  return (
    <p className="artifact-empty">
      A preview is not available for this file. Download it to open in a
      compatible app.
    </p>
  )
}
